import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { IconAlertTriangle, IconChevronDown, IconChevronRight, IconExternalLink, IconFileX } from "@tabler/icons-react"

export interface Entry {
  signed_blob_id: string
  filename: string
  pdf_url: string
  error: string | null
  ready: boolean
  serie?: string | null
  number?: string | null
  currency?: string | null
  invoice_date?: string | null
  due_date?: string | null
  total_amount?: number | null
  customer_name?: string | null
  customer_tax_id?: string | null
  customer_id?: number | null
  match_strategy?: string
  missing?: string[]
  duplicate_of?: string | null
}

export interface RowValues {
  selected: boolean
  customer_id: string
  cfe_serie: string
  cfe_number: string
  currency: string
  invoice_date: string
  due_date: string
  total_amount: string
  status: string
  notes: string
  sent_to_accounting: boolean
}

interface Option {
  id: number
  name: string
}

interface StatusOption {
  value: string
  label: string
}

interface Props {
  entry: Entry
  values: RowValues
  expanded: boolean
  customers: Option[]
  currencies: string[]
  statuses: StatusOption[]
  onToggleExpanded: () => void
  onChange: (field: keyof RowValues, value: string | boolean) => void
}

function formatAmount(value: string, currency: string) {
  const parsed = Number(value)
  if (!value || Number.isNaN(parsed)) return "-"
  return `${currency.toUpperCase()} ${parsed.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export default function InvoiceReviewRow({
  entry,
  values,
  expanded,
  customers,
  currencies,
  statuses,
  onToggleExpanded,
  onChange,
}: Props) {
  const customerName = customers.find((c) => String(c.id) === values.customer_id)?.name
  const problems: string[] = []

  if (entry.error) problems.push(entry.error)
  if (entry.duplicate_of) problems.push(`Already recorded as ${entry.duplicate_of}`)
  if (!entry.error && !values.customer_id) problems.push("No customer matched")
  if (entry.missing?.length) problems.push(`Could not read ${entry.missing.join(", ")}`)

  return (
    <div className="border-b last:border-b-0">
      <div className="flex items-center gap-3 px-4 py-3">
        <Checkbox
          checked={values.selected}
          disabled={!!entry.error || !!entry.duplicate_of}
          onCheckedChange={(checked) => onChange("selected", checked === true)}
          aria-label={`Include ${entry.filename}`}
        />

        <button
          type="button"
          onClick={onToggleExpanded}
          className="hover:bg-muted/50 -mx-1 flex min-w-0 flex-1 items-center gap-3 rounded px-1 py-1 text-left"
          aria-expanded={expanded}
        >
          {expanded ? (
            <IconChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
          ) : (
            <IconChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
          )}

          {entry.error ? <IconFileX className="text-destructive h-4 w-4 shrink-0" /> : null}

          <span className="w-28 shrink-0 font-mono text-xs">
            {entry.serie && entry.number ? `${entry.serie}-${entry.number}` : "-"}
          </span>

          <span className="min-w-0 flex-1 truncate text-sm">
            {customerName || entry.customer_name || entry.filename}
          </span>

          <span className="w-36 shrink-0 text-right font-mono text-sm">
            {formatAmount(values.total_amount, values.currency)}
          </span>

          <span className="text-muted-foreground w-24 shrink-0 text-right text-xs">{values.invoice_date || "-"}</span>
        </button>

        {problems.length > 0 && (
          <Badge variant="outline" className="shrink-0 border-amber-300 text-amber-700 dark:text-amber-400">
            <IconAlertTriangle className="mr-1 h-3 w-3" />
            Needs attention
          </Badge>
        )}
      </div>

      {expanded && (
        <div className="bg-muted/30 border-t px-4 py-4">
          {problems.length > 0 && (
            <div className="mb-4 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/30">
              <IconAlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <ul className="space-y-1 text-sm text-amber-800 dark:text-amber-300">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,30rem)]">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-muted-foreground truncate text-xs">{entry.filename}</p>
                <Button variant="ghost" size="sm" asChild>
                  <a href={entry.pdf_url} target="_blank" rel="noopener noreferrer">
                    <IconExternalLink className="mr-1 h-4 w-4" />
                    Open
                  </a>
                </Button>
              </div>
              <iframe
                src={entry.pdf_url}
                title={`Invoice PDF: ${entry.filename}`}
                className="bg-muted h-[38rem] max-h-[70vh] w-full rounded-md border"
              />
            </div>

            {entry.error ? (
              <p className="text-muted-foreground text-sm">
                This file could not be read, so there is nothing to review. Open it above to check it is a valid invoice
                PDF.
              </p>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor={`customer-${entry.signed_blob_id}`}>Customer</Label>
                  <Select value={values.customer_id} onValueChange={(v) => onChange("customer_id", v)}>
                    <SelectTrigger id={`customer-${entry.signed_blob_id}`}>
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {entry.customer_name && (
                    <p className="text-muted-foreground text-xs">
                      PDF says &quot;{entry.customer_name}&quot;
                      {entry.customer_id ? ` - matched by ${entry.match_strategy?.replace(/_/g, " ")}` : ""}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor={`serie-${entry.signed_blob_id}`}>CFE series</Label>
                    <Input
                      id={`serie-${entry.signed_blob_id}`}
                      value={values.cfe_serie}
                      onChange={(e) => onChange("cfe_serie", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`number-${entry.signed_blob_id}`}>CFE number</Label>
                    <Input
                      id={`number-${entry.signed_blob_id}`}
                      value={values.cfe_number}
                      onChange={(e) => onChange("cfe_number", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor={`total-${entry.signed_blob_id}`}>Total</Label>
                    <Input
                      id={`total-${entry.signed_blob_id}`}
                      type="number"
                      step="0.01"
                      value={values.total_amount}
                      onChange={(e) => onChange("total_amount", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`currency-${entry.signed_blob_id}`}>Currency</Label>
                    <Select value={values.currency} onValueChange={(v) => onChange("currency", v)}>
                      <SelectTrigger id={`currency-${entry.signed_blob_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c.toUpperCase()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor={`date-${entry.signed_blob_id}`}>Invoice date</Label>
                    <Input
                      id={`date-${entry.signed_blob_id}`}
                      type="date"
                      value={values.invoice_date}
                      onChange={(e) => onChange("invoice_date", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`due-${entry.signed_blob_id}`}>Due date</Label>
                    <Input
                      id={`due-${entry.signed_blob_id}`}
                      type="date"
                      value={values.due_date}
                      onChange={(e) => onChange("due_date", e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`status-${entry.signed_blob_id}`}>Status</Label>
                  <Select value={values.status} onValueChange={(v) => onChange("status", v)}>
                    <SelectTrigger id={`status-${entry.signed_blob_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`notes-${entry.signed_blob_id}`}>Description</Label>
                  <Textarea
                    id={`notes-${entry.signed_blob_id}`}
                    value={values.notes}
                    onChange={(e) => onChange("notes", e.target.value)}
                    placeholder="Also written to column A of the Revenue sheet"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`accounting-${entry.signed_blob_id}`}
                    checked={values.sent_to_accounting}
                    onCheckedChange={(checked) => onChange("sent_to_accounting", checked === true)}
                  />
                  <Label htmlFor={`accounting-${entry.signed_blob_id}`} className="font-normal">
                    Sent to accounting
                  </Label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
